import { lazy, Suspense, useState, useCallback } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { PlatformRoute } from "@/components/PlatformRoute";
import OfflineBanner from "@/components/OfflineBanner";
import { useNativeBackButton } from "@/hooks/useNativeBackButton";
import { useDeepLinks } from "@/hooks/useDeepLinks";
import AnimatedSplash from "@/components/AnimatedSplash";

// Lazy-loaded pages
const Landing = lazy(() => import("./pages/Landing"));
const CadastroRapido = lazy(() => import("./pages/CadastroRapido"));
const Auth = lazy(() => import("./pages/Auth"));
const Assinar = lazy(() => import("./pages/Assinar"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Home = lazy(() => import("./pages/Home"));
const Agenda = lazy(() => import("./pages/Agenda"));
const Contribuir = lazy(() => import("./pages/Contribuir"));
const Doacoes = lazy(() => import("./pages/Doacoes"));
const Avisos = lazy(() => import("./pages/Avisos"));
const Espiritualidade = lazy(() => import("./pages/Espiritualidade"));
const Notificacoes = lazy(() => import("./pages/Notificacoes"));
const LiturgiaDiaria = lazy(() => import("./pages/LiturgiaDiaria"));
const SantoDoDia = lazy(() => import("./pages/SantoDoDia"));
const Biblia = lazy(() => import("./pages/Biblia"));
const BibliaLivro = lazy(() => import("./pages/BibliaLivro"));
const BibliaCapitulo = lazy(() => import("./pages/BibliaCapitulo"));
const Oracoes = lazy(() => import("./pages/Oracoes"));
const TercoInterativo = lazy(() => import("./pages/TercoInterativo"));
const ParoquiasProximas = lazy(() => import("./pages/ParoquiasProximas"));
const Perfil = lazy(() => import("./pages/Perfil"));
const SelecionarParoquia = lazy(() => import("./pages/SelecionarParoquia"));
const RecuperarSenha = lazy(() => import("./pages/RecuperarSenha"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ConfirmarEmail = lazy(() => import("./pages/ConfirmarEmail"));
const CompletarPerfil = lazy(() => import("./pages/CompletarPerfil"));
const VincularParoquia = lazy(() => import("./pages/VincularParoquia"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const Manual = lazy(() => import("./pages/Manual"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CadastroParoquia = lazy(() => import("./pages/CadastroParoquia"));
const ComprarTicket = lazy(() => import("./pages/ComprarTicket"));
const MeusIngressos = lazy(() => import("./pages/MeusIngressos"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Novena = lazy(() => import("./pages/Novena"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminAvisos = lazy(() => import("./pages/admin/Avisos"));
const AdminAgenda = lazy(() => import("./pages/admin/Agenda"));
const AdminTickets = lazy(() => import("./pages/admin/Tickets"));
const AdminDoacoes = lazy(() => import("./pages/admin/Doacoes"));
const AdminConfiguracoes = lazy(() => import("./pages/admin/Configuracoes"));
const AdminOnboarding = lazy(() => import("./pages/admin/Onboarding"));
const AdminCampanhas = lazy(() => import("./pages/admin/Campanhas"));
const AdminMembros = lazy(() => import("./pages/admin/Membros"));
const AdminQRCode = lazy(() => import("./pages/admin/QRCodeParoquia"));
const AdminComunidades = lazy(() => import("./pages/admin/Comunidades"));
const AdminValidarTicket = lazy(() => import("./pages/admin/ValidarTicket"));
const AdminManual = lazy(() => import("./pages/admin/ManualAdmin"));
const AdminGerenciarPlano = lazy(() => import("./pages/admin/GerenciarPlano"));
const AdminCobrancas = lazy(() => import("./pages/admin/Cobrancas"));

// Platform (Super Admin) pages
const PlatformDashboard = lazy(() => import("./pages/platform/Dashboard"));
const PlatformPlanos = lazy(() => import("./pages/platform/Planos"));
const PlatformParoquias = lazy(() => import("./pages/platform/Paroquias"));
const PlatformVersoes = lazy(() => import("./pages/platform/Versoes"));
const PlatformSuporte = lazy(() => import("./pages/platform/Suporte"));
const PlatformCobrancas = lazy(() => import("./pages/platform/Cobrancas"));
const PlatformConfiguracoes = lazy(() => import("./pages/platform/Configuracoes"));
const PlatformEmailMarketing = lazy(() => import("./pages/platform/EmailMarketing"));
const PlatformWhatsApp = lazy(() => import("./pages/platform/WhatsApp"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min – avoid refetching on every mount
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

/** Hooks that need router context */
const NativeIntegrations = () => {
  useNativeBackButton();
  useDeepLinks();
  return null;
};

const App = () => {
  const [splashDone, setSplashDone] = useState(!Capacitor.isNativePlatform());
  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            {!splashDone && <AnimatedSplash onComplete={handleSplashComplete} />}
            <Toaster />
            <Sonner />
            <OfflineBanner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <GoogleAnalytics />
              <NativeIntegrations />

              <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                <Routes>
                  <Route path="/" element={
                    Capacitor.isNativePlatform()
                      ? <Navigate to="/auth" replace />
                      : (() => {
                        if (window.matchMedia("(display-mode: standalone)").matches) {
                          return <Navigate to="/auth" replace />;
                        }
                        return <Landing />;
                      })()
                  } />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/login" element={<Auth />} />
                  <Route path="/assinar" element={<Assinar />} />
                  <Route path="/assinar/:planoId" element={<Assinar />} />
                  <Route path="/recuperar-senha" element={<RecuperarSenha />} />
                  <Route path="/confirmar-email" element={<ConfirmarEmail />} />
                  <Route path="/checkout-success" element={<CheckoutSuccess />} />
                  <Route path="/termos-de-uso" element={<TermosDeUso />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                  <Route path="/manual" element={<Manual />} />
                  <Route path="/cadastro-paroquia" element={<CadastroParoquia />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/completar-perfil" element={<ProtectedRoute><CompletarPerfil /></ProtectedRoute>} />
                  <Route path="/vincular-paroquia" element={<ProtectedRoute><VincularParoquia /></ProtectedRoute>} />
                  <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                  <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                  <Route path="/contribuir" element={<ProtectedRoute><Contribuir /></ProtectedRoute>} />
                  <Route path="/doacoes" element={<ProtectedRoute><Doacoes /></ProtectedRoute>} />
                  <Route path="/avisos" element={<ProtectedRoute><Avisos /></ProtectedRoute>} />
                  <Route path="/espiritualidade" element={<ProtectedRoute><Espiritualidade /></ProtectedRoute>} />
                  <Route path="/liturgia-diaria" element={<ProtectedRoute><LiturgiaDiaria /></ProtectedRoute>} />
                  <Route path="/santo-do-dia" element={<ProtectedRoute><SantoDoDia /></ProtectedRoute>} />
                  <Route path="/biblia" element={<ProtectedRoute><Biblia /></ProtectedRoute>} />
                  <Route path="/biblia/:abbrev" element={<ProtectedRoute><BibliaLivro /></ProtectedRoute>} />
                  <Route path="/biblia/:abbrev/:chapter" element={<ProtectedRoute><BibliaCapitulo /></ProtectedRoute>} />
                  <Route path="/oracoes" element={<ProtectedRoute><Oracoes /></ProtectedRoute>} />
                  <Route path="/novena/:id" element={<ProtectedRoute><Novena /></ProtectedRoute>} />
                  <Route path="/terco-interativo" element={<ProtectedRoute><TercoInterativo /></ProtectedRoute>} />
                  <Route path="/paroquias-proximas" element={<ProtectedRoute><ParoquiasProximas /></ProtectedRoute>} />
                  <Route path="/notificacoes" element={<ProtectedRoute><Notificacoes /></ProtectedRoute>} />
                  <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
                  <Route path="/selecionar-paroquia" element={<ProtectedRoute><SelecionarParoquia /></ProtectedRoute>} />
                  <Route path="/evento/:eventoId/tickets" element={<ProtectedRoute><ComprarTicket /></ProtectedRoute>} />
                  <Route path="/meus-ingressos" element={<ProtectedRoute><MeusIngressos /></ProtectedRoute>} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="/admin/avisos" element={<AdminRoute><AdminAvisos /></AdminRoute>} />
                  <Route path="/admin/agenda" element={<AdminRoute><AdminAgenda /></AdminRoute>} />
                  <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
                  <Route path="/admin/doacoes" element={<AdminRoute><AdminDoacoes /></AdminRoute>} />
                  <Route path="/admin/configuracoes" element={<AdminRoute><AdminConfiguracoes /></AdminRoute>} />
                  <Route path="/admin/campanhas" element={<AdminRoute><AdminCampanhas /></AdminRoute>} />
                  <Route path="/admin/membros" element={<AdminRoute><AdminMembros /></AdminRoute>} />
                  <Route path="/admin/onboarding" element={<ProtectedRoute><AdminOnboarding /></ProtectedRoute>} />
                  <Route path="/admin/comunidades" element={<AdminRoute><AdminComunidades /></AdminRoute>} />
                  <Route path="/admin/qrcode" element={<AdminRoute><AdminQRCode /></AdminRoute>} />
                  <Route path="/admin/validar-ticket" element={<AdminRoute><AdminValidarTicket /></AdminRoute>} />
                  <Route path="/admin/manual" element={<AdminRoute><AdminManual /></AdminRoute>} />
                  <Route path="/admin/gerenciar-plano" element={<AdminRoute><AdminGerenciarPlano /></AdminRoute>} />
                  <Route path="/admin/cobrancas" element={<AdminRoute><AdminCobrancas /></AdminRoute>} />

                  {/* Platform (Super Admin) Routes */}
                  <Route path="/platform" element={<PlatformRoute><PlatformDashboard /></PlatformRoute>} />
                  <Route path="/platform/paroquias" element={<PlatformRoute><PlatformParoquias /></PlatformRoute>} />
                  <Route path="/platform/planos" element={<PlatformRoute><PlatformPlanos /></PlatformRoute>} />
                  <Route path="/platform/cobrancas" element={<PlatformRoute><PlatformCobrancas /></PlatformRoute>} />
                  <Route path="/platform/versoes" element={<PlatformRoute><PlatformVersoes /></PlatformRoute>} />
                  <Route path="/platform/email" element={<PlatformRoute><PlatformEmailMarketing /></PlatformRoute>} />
                  <Route path="/platform/whatsapp" element={<PlatformRoute><PlatformWhatsApp /></PlatformRoute>} />
                  <Route path="/platform/suporte" element={<PlatformRoute><PlatformSuporte /></PlatformRoute>} />
                  <Route path="/platform/configuracoes" element={<PlatformRoute><PlatformConfiguracoes /></PlatformRoute>} />

                  <Route path="/cadastro-fiel/:paroquiaId" element={<CadastroRapido />} />
                  <Route path="/app" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
